"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function RecursiveMenuLinks({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-border/40"}>
      {items.map((item, i) => <li key={item.id || i} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 block py-0.5">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMenuLinks items={item.children} level={level + 1} />}
        </li>)}
    </ul>;
}
function FooterNavContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5">
        <li data-index={0}>
          <Link to="/HomePage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Home
          </Link>
        </li>
        <li data-index={1}>
          <Link to="/ServicesPage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Services
          </Link>
        </li>
        <li data-index={2}>
          <Link to="/AboutUsPage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            About Us
          </Link>
        </li>
        <li data-index={3}>
          <Link to="/OrderPage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Reserve a Ride
          </Link>
        </li>
      </ul>;
  }
  return <RecursiveMenuLinks items={menuItems} />;
}
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <footer id="footer" data-nav="dark" className="dark bg-background text-foreground border-t border-border/60 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl pt-16 pb-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand Identity & Summary */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="inline-block transition-opacity hover:opacity-90">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              Executive sedans and SUVs for airport transfers, hourly chauffeur hire, and long-distance travel across Chicago and the Midwest.
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>City of Chicago and IDOT licensed</span>
            </div>
          </div>

          {/* Quick Navigation Menu */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-foreground">
              Navigation
            </h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterNavContent />
            </MenuProvider>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-foreground">
              Direct Contact
            </h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5 text-muted-foreground">
                <Phone className="size-4 text-primary shrink-0 mt-0.5" />
                <a href="tel:3125550100" className="hover:text-foreground transition-colors duration-200">
                  (312) 555-0100
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-muted-foreground">
                <Mail className="size-4 text-primary shrink-0 mt-0.5" />
                <a href="mailto:contact@santalimo.com" className="hover:text-foreground transition-colors duration-200">
                  contact@santalimo.com
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-muted-foreground">
                <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                <span>Chicago, Illinois, USA</span>
              </li>
            </ul>
          </div>

          {/* Operating Hours & Dispatch */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-foreground">
              Operations
            </h3>
            <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <Clock className="size-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">24/7 Dispatch</p>
                <p className="text-xs text-muted-foreground mt-0.5">Booking support available around the clock</p>
              </div>
            </div>
          </div>
        </motion.div>

        <Separator className="my-10 bg-border/60" />

        {/* Bottom Legal Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            {"© "}{new Date().getFullYear()}{" SANTA LIMO. All rights reserved."}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/PrivacyStatementPage" className="hover:text-foreground transition-colors duration-200">
              Privacy Statement
            </Link>
          </div>
        </div>
      </div>
    </footer>;
}